import { SearchIcon } from 'flyt/ui/Icons/Icons'
import type { InputProps } from './Input.types'

/**
 * input — controlled single-line text input primitive.
 *
 * Renders one native <input>; holds no state, keeps no internal copy of the
 * value. Every accepted native attribute is forwarded verbatim. The invalid
 * state changes only the border treatment and sets aria-invalid; when not
 * invalid the attribute is entirely absent.
 *
 * A SEARCH FIELD CARRIES ITS OWN GLYPH, INSIDE. The magnifier is not
 * decoration a caller adds — it is what a search field looks like everywhere,
 * so the primitive draws it from the `type` it is already given: derived during
 * render from a prop it already holds, so there is no flag, no slot, and no
 * call site that can forget it. Beside the field the glyph was a loose icon on
 * the row that every caller had to align and space; inside it, the field reads
 * as one control and the row around it keeps its width for the controls that
 * need it.
 */
export function Input({ invalid = false, ...rest }: InputProps) {
  const searching = rest.type === 'search'
  const field = (
    <input
      {...rest}
      aria-invalid={invalid || undefined}
      className={`w-full rounded-md border bg-surface py-2 text-sm text-text transition-colors placeholder:text-text-muted focus:outline-2 focus:outline-accent disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-text-muted ${
        searching ? 'pr-3 pl-9' : 'px-3'
      } ${invalid ? 'border-danger' : 'border-border-control enabled:hover:border-border-hover'}`}
    />
  )
  if (!searching) {
    return field
  }
  // The glyph sits in the left padding the field reserved for it, vertically
  // centred and click-through (`pointer-events-none`), so pressing it lands on
  // the input and focuses it exactly as pressing the text would.
  return (
    <span className="relative block w-full">
      <SearchIcon
        width={16}
        height={16}
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-text-muted"
      />
      {field}
    </span>
  )
}
